import React, { Component, Fragment } from 'react'
import { ApprovalWorkflowSettingsForm } from './approval-workflow-settings-form'
import { SubstatusAliasSettingsForm } from './substatus-alias-settings-form'
import { ConfigurationPanel } from '../../legacy-design-system/configuration-panel'
import { Box, Row } from '../../legacy-design-system/form'
import { Severity } from '../vulnerability-severity'
import { ApprovalWorkflowSettings, SubstatusAliasSettings } from '.'
import { css } from '@emotion/css'
import { FormattedMessage } from 'react-intl'
import { Button } from '@contrast-security-inc/design-system-buttons'
import { ConfirmationDialog } from '../../legacy-design-system/modal'
import { ReactQueryClient } from '../../ng/react-query-helpers'
import { QueryClient } from 'react-query'
import { Query } from '../../hooks/queries'

export const buttonSaveVulnerabilityOptionsTestId =
  'button-saveVulnerabilityOptionsTestId'

export const aliasCharsAllowed = 25

export interface VulnerabilityBehaviorPanelProps {
  readonly workFlowSettings: ApprovalWorkflowSettings
  readonly aliasSettings: SubstatusAliasSettings
  readonly onSubmit: (
    settings: ApprovalWorkflowSettings & SubstatusAliasSettings
  ) => void
}

export interface State {
  readonly workflowEnabled: boolean
  readonly statuses: string[]
  readonly severities: Severity[]
  readonly aliasEnabled: boolean
  readonly alias: string
  readonly formHasChanged: boolean
  readonly confirmModalIsOpen: boolean
  readonly isBtnDisabled: boolean
}

export class VulnerabilityBehaviorPanel extends Component<
  VulnerabilityBehaviorPanelProps,
  State
> {
  constructor(props: VulnerabilityBehaviorPanelProps) {
    super(props)

    this.state = getInitialState(props.workFlowSettings, props.aliasSettings)
  }

  public render() {
    const {
      workflowEnabled,
      statuses,
      severities,
      aliasEnabled,
      alias,
      isBtnDisabled
    } = this.state

    const savedAlias =
      this.props.aliasSettings.alias.length > 0
        ? this.props.aliasSettings.alias
        : undefined

    return (
      <ReactQueryClient>
        {(queryClient) => (
          <ConfigurationPanel title="title_vulnerabilityBehavior">
            <div
              className={css`
                padding: 10px 11px 13px 0px;
              `}
            >
              <Row>
                <Box basis={7}>
                  <div
                    className={css`
                      margin-top: 15px;
                      margin-left: 12px;
                    `}
                  >
                    <ApprovalWorkflowSettingsForm
                      enabled={workflowEnabled}
                      statuses={statuses}
                      severities={severities}
                      onEnabling={this.setworkflowEnabled}
                      onStatusChange={(statuses) => {
                        this.setState({ statuses, formHasChanged: true }, () =>
                          this.updateBtnStatus()
                        )
                      }}
                      onSeveritiesChange={(severities) => {
                        this.setState(
                          { severities, formHasChanged: true },
                          () => this.updateBtnStatus()
                        )
                      }}
                    />

                    <SubstatusAliasSettingsForm
                      enabled={aliasEnabled}
                      alias={alias}
                      savedAlias={savedAlias}
                      onEnabling={this.setAliasEnabled}
                      onAliasChange={(alias) => {
                        if (alias.length <= aliasCharsAllowed) {
                          this.setState(
                            {
                              alias: capitalize(alias),
                              formHasChanged: true
                            },
                            () => this.updateBtnStatus()
                          )
                        }
                      }}
                    />
                  </div>
                </Box>
              </Row>

              <div
                className={css`
                  display: flex;
                  flex-direction: row-reverse;
                `}
              >
                <FormattedMessage
                  id={'action_save'}
                  children={(saveMessage) => (
                    <Fragment>
                      <Button
                        data-testid={buttonSaveVulnerabilityOptionsTestId}
                        isDisabled={isBtnDisabled}
                        onClick={() => {
                          if (
                            this.shouldOpenConfirmModal(
                              aliasEnabled,
                              savedAlias,
                              alias
                            )
                          ) {
                            this.openConfirmModal()
                          } else {
                            this.save(queryClient)
                          }
                        }}
                      >
                        {saveMessage}
                      </Button>
                      <ConfirmationDialog
                        size="small"
                        isOpen={this.state.confirmModalIsOpen}
                        renderBody={() => (
                          <FormattedMessage
                            id="detail_confirmCustomSubstatusAlias"
                            values={{
                              old_alias: <b>{savedAlias}</b>,
                              alias: <b>{alias}</b>
                            }}
                          />
                        )}
                        renderCancelMessage={() => (
                          <FormattedMessage id="action_cancel" />
                        )}
                        renderTitle={() => (
                          <FormattedMessage id="title_changeCustomField" />
                        )}
                        renderConfirmMessage={() => (
                          <FormattedMessage id="action_save" />
                        )}
                        onCancel={this.closeConfirmModal}
                        onConfirm={() => {
                          this.closeConfirmModal()
                          this.save(queryClient)
                        }}
                      />
                    </Fragment>
                  )}
                />
              </div>
            </div>
          </ConfigurationPanel>
        )}
      </ReactQueryClient>
    )
  }

  public componentDidUpdate(prevProps: VulnerabilityBehaviorPanelProps) {
    if (this.propsChanged(prevProps)) {
      this.setState(
        getInitialState(this.props.workFlowSettings, this.props.aliasSettings)
      )
    }
  }

  private propsChanged = (
    prevProps: VulnerabilityBehaviorPanelProps
  ): boolean => {
    return this.props.workFlowSettings !== prevProps.workFlowSettings
  }

  private setFormHasChanged(formHasChanged: boolean) {
    this.setState({ formHasChanged }, () => this.updateBtnStatus())
  }

  private setworkflowEnabled = (workflowEnabled: boolean) => {
    if (!workflowEnabled) {
      this.setState({
        workflowEnabled,
        statuses: [],
        severities: []
      })
    } else {
      this.setState({ workflowEnabled, formHasChanged: true })
    }
    this.setFormHasChanged(true)
  }

  private setAliasEnabled = (aliasEnabled: boolean) => {
    if (!aliasEnabled) {
      this.setState({
        aliasEnabled,
        alias: ''
      })
    } else {
      this.setState({ aliasEnabled })
    }
    this.setFormHasChanged(true)
  }

  private save = (queryClient: QueryClient) => {
    const { workflowEnabled, statuses, severities, aliasEnabled, alias } =
      this.state

    this.props.onSubmit({
      workflowEnabled,
      statuses,
      severities: severities.map((severity) => severity.name),
      aliasEnabled,
      alias
    })
    this.setState({ isBtnDisabled: true })
    // invalidate react-query cache containg the old approval workflow setting
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    queryClient.invalidateQueries([Query.APPROVAL_WORKFLOW_FEATURE], {
      exact: false
    })
  }

  private closeConfirmModal = () => this.setState({ confirmModalIsOpen: false })
  private openConfirmModal = () => this.setState({ confirmModalIsOpen: true })
  // is true when changing from one custom alias to another custom alias
  private shouldOpenConfirmModal = (aliasEnabled, savedAlias, currentAlias) => {
    return aliasEnabled && savedAlias && savedAlias !== currentAlias
  }

  private updateBtnStatus(): void {
    this.setState({
      isBtnDisabled:
        !this.state.formHasChanged ||
        (this.state.aliasEnabled && this.state.alias.length === 0)
    })
  }
}

export function getInitialState(
  initialWorkflowSettings?: VulnerabilityBehaviorPanelProps['workFlowSettings'],
  initialAliasSettings?: VulnerabilityBehaviorPanelProps['aliasSettings']
) {
  const state: any = {
    workflowEnabled: false,
    statuses: [],
    severities: [],
    aliasEnabled: false,
    alias: '',
    formHasChanged: false,
    confirmModalIsOpen: false,
    isBtnDisabled: true
  }

  if (initialWorkflowSettings) {
    const { workflowEnabled, statuses, severities } = initialWorkflowSettings

    state.workflowEnabled = workflowEnabled
    state.statuses = statuses
    state.severities = severities.map(Severity.fromName)
  }

  if (initialAliasSettings) {
    const { alias, aliasEnabled } = initialAliasSettings

    state.alias = alias
    state.aliasEnabled = aliasEnabled
  }

  return state
}

// Capitalized for consistency and sorting purposes
const capitalize = (string: string) =>
  string.replace(/^\w/, (c) => c.toUpperCase())
